import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button.tsx";
import { Link } from "@/libraries/design/components/link/src/link.tsx";
import { Modal } from "@/libraries/design/components/modal/src/modal.tsx";
import { wacToken } from "@/libraries/design/foundations/index.ts";

// Typography color constants matching typography-styles.tsx
const PRIMARY_BLACK = "#101214";
const NEUTRAL_LIGHT = "#292A2E";

import {
    useClearSelectedFeature,
    useSelectedFeatureData
} from "../context/hooks.ts";

import { FeatureDescription } from "./feature-description.tsx";
import { StatusLozenge } from "./status-lozenge.tsx";

export const SelectedFeatureModal = () => {
    const selectedFeature = useSelectedFeatureData();
    const clearSelectedFeature = useClearSelectedFeature();
    const [isCopied, setIsCopied] = useState(false);

    const intl = useIntl();

    /**
     * NOTE: We cannot use className="eyebrow-lg" and className="h4" here because:
     *
     * 1. The Modal component wraps the title prop in <p className="card-title">, which creates
     *    invalid HTML nesting when we pass a fragment with <p> and <h4> elements. Browsers
     *    auto-correct this invalid structure, which breaks CSS selector matching.
     *
     * 2. When using both className and css prop together, Compiled CSS may not properly merge
     *    the styles. The css prop styles can override or interfere with className-based styles
     *    from TypographyStyles, even when they don't explicitly conflict.
     *
     * 3. CSS specificity issues: The parent .card-title class combined with how Compiled CSS
     *    generates atomic CSS classes can cause the TypographyStyles className selectors to
     *    not match correctly in this nested context.
     *
     * Therefore, we manually apply the eyebrow-lg and h4 styles via the css prop to ensure
     * they work correctly. These styles match the definitions in typography-styles.tsx.
     */
    const header = (
        <>
            <p
                css={css({
                    // eyebrow-lg styles (from typography-styles.tsx)
                    fontFamily: wacToken("font.charlie.text"),
                    fontWeight: 700,
                    fontSize: "1rem",
                    lineHeight: "1.5rem",
                    textTransform: "uppercase",
                    color: NEUTRAL_LIGHT,
                    // Layout
                    margin: 0,
                    marginBlockEnd: token("space.100")
                })}
            >
                {selectedFeature?.category.roadmapCategoryNameExternal}
            </p>
            <h4
                css={css({
                    // h4 styles (from typography-styles.tsx)
                    fontFamily: wacToken("font.charlie.display"),
                    fontWeight: 700,
                    color: PRIMARY_BLACK,
                    fontSize: "1.25rem",
                    lineHeight: "1.625rem",
                    // Responsive styles
                    [media.only.sm]: {
                        fontSize: "1.375rem",
                        lineHeight: "1.75rem"
                    },
                    [media.above.md]: {
                        fontSize: "1.5rem",
                        lineHeight: "1.875rem"
                    },
                    // Layout
                    margin: 0
                })}
            >
                {selectedFeature?.title}
            </h4>
        </>
    );

    return (
        <Modal
            isOpen={!!selectedFeature}
            closeModal={clearSelectedFeature}
            title={header}
            width="medium"
            closeLabel={intl.formatMessage({
                id: "SelectedFeatureModal.close",
                defaultMessage: "Close"
            })}
        >
            {selectedFeature && (
                <>
                    <FeatureDescription content={selectedFeature.description} />

                    {selectedFeature.relatedLinksCollection?.items &&
                        selectedFeature.relatedLinksCollection.items.length >
                            0 && (
                            <div
                                css={css({
                                    marginBlockStart: token("space.200"),
                                    marginBlockEnd: token("space.200")
                                })}
                            >
                                <ul
                                    css={css({
                                        listStyle: "none",
                                        padding: 0,
                                        margin: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: token("space.100")
                                    })}
                                >
                                    {selectedFeature.relatedLinksCollection.items.map(
                                        (link, index) => (
                                            <li key={index}>
                                                <Link
                                                    text={link.ctaButtonText}
                                                    href={link.ctaUrl}
                                                    type="primary"
                                                    aria-label={
                                                        link.ctaButtonText
                                                    }
                                                />
                                            </li>
                                        )
                                    )}
                                </ul>
                            </div>
                        )}

                    <div
                        css={css({
                            display: "flex",
                            flexDirection: "column",
                            gap: token("space.200")
                        })}
                    >
                        <div>
                            {intl.formatMessage({
                                id: "SelectedFeatureModal.share",
                                defaultMessage: "Share this update:"
                            })}
                            <div
                                css={css({
                                    display: "flex",
                                    gap: token("space.200"),
                                    alignItems: "center"
                                })}
                            >
                                <input
                                    readOnly={true}
                                    value={getShareableUrl()}
                                    css={css({
                                        flex: 1,
                                        minWidth: 0,
                                        height: token("space.400"),
                                        border: `2px solid ${wacToken(
                                            "color.neutral.light400"
                                        )}`,
                                        borderRadius:
                                            token("border.radius.200"),
                                        padding: `0 ${token("space.100")}`,
                                        position: "relative",
                                        insetBlockStart: `-${token(
                                            "space.100"
                                        )}`
                                    })}
                                />
                                <Button
                                    size="small"
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            getShareableUrl()
                                        );
                                        setIsCopied(true);
                                        setTimeout(() => {
                                            setIsCopied(false);
                                        }, 2000);
                                    }}
                                >
                                    {isCopied
                                        ? intl.formatMessage({
                                              id: "SelectedFeatureModal.linkCopied",
                                              defaultMessage: "Link Copied"
                                          })
                                        : intl.formatMessage({
                                              id: "SelectedFeatureModal.copyLink",
                                              defaultMessage: "Copy link"
                                          })}
                                </Button>
                            </div>
                        </div>

                        <div css={css({ display: "flex", gap: 4 })}>
                            <StatusLozenge
                                text={
                                    selectedFeature.status
                                        .roadmapStatusNameExternal
                                }
                            />
                            <span>{selectedFeature.timeFrame}</span>
                        </div>

                        <div>
                            {intl.formatMessage({
                                id: "SelectedFeatureModal.availableIn",
                                defaultMessage: "This update is available in:"
                            })}
                            &nbsp;
                            {selectedFeature.appsCollection.items
                                .map((item) => item.roadmapAppNameExternal)
                                .join(", ")}
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
};

function getShareableUrl() {
    return window.location.href;
}
